'use client';

import { createClient } from '@/lib/supabase/client';

export interface StoredTrade {
  id: string;
  date: string;
  dateISO: string;
  symbol: string;
  direction: 'Long' | 'Short';
  entry: number;
  exit: number;
  size: number;
  pnl: number;
  pnlPct: number;
  rr: number;
  strategy: string;
  timeframe: string;
  stopLoss?: number;
  takeProfit?: number;
  rating: number;
  hasScreenshot: boolean;
  notes: string;
  lessons: string;
  status: 'Reviewed' | 'Pending Review' | 'Flagged';
}

const STORAGE_KEY = 'trade_journal';

const SEED_TRADES: StoredTrade[] = [
  { id: 'trade-001', date: 'Aug 25', symbol: 'NVDA', direction: 'Long', entry: 128.40, exit: 134.20, size: 150, pnl: 870, pnlPct: 4.52, rr: 2.9, strategy: 'Breakout', timeframe: '15m', rating: 5, hasScreenshot: true, notes: 'Clean break of daily resistance. Held through consolidation.', lessons: '', status: 'Reviewed', dateISO: '2026-08-25' },
  { id: 'trade-002', date: 'Aug 25', symbol: 'SPY', direction: 'Short', entry: 548.80, exit: 545.10, size: 200, pnl: 740, pnlPct: 0.67, rr: 2.4, strategy: 'VWAP Reclaim', timeframe: '5m', rating: 4, hasScreenshot: true, notes: 'Failed VWAP reclaim on 5m. Shorted into weakness.', lessons: '', status: 'Reviewed', dateISO: '2026-08-25' },
  { id: 'trade-003', date: 'Aug 22', symbol: 'TSLA', direction: 'Long', entry: 241.50, exit: 256.80, size: 80, pnl: 1224, pnlPct: 6.33, rr: 3.2, strategy: 'Momentum', timeframe: '15m', rating: 5, hasScreenshot: true, notes: 'Earnings momentum continuation. Perfect entry on 15m pullback.', lessons: '', status: 'Reviewed', dateISO: '2026-08-22' },
  { id: 'trade-004', date: 'Aug 22', symbol: 'AAPL', direction: 'Long', entry: 212.30, exit: 209.80, size: 120, pnl: -300, pnlPct: -1.18, rr: -0.8, strategy: 'Breakout', timeframe: '1H', rating: 2, hasScreenshot: false, notes: 'False breakout. Should have waited for volume confirmation.', lessons: 'Wait for volume confirmation before entering breakouts.', status: 'Flagged', dateISO: '2026-08-22' },
  { id: 'trade-005', date: 'Aug 21', symbol: 'META', direction: 'Short', entry: 582.40, exit: 574.60, size: 50, pnl: 390, pnlPct: 1.34, rr: 1.9, strategy: 'Gap Fill', timeframe: '30m', rating: 3, hasScreenshot: true, notes: 'Gap fill setup worked but I exited early.', lessons: 'Hold to the full target next time.', status: 'Reviewed', dateISO: '2026-08-21' },
  { id: 'trade-006', date: 'Aug 20', symbol: 'AMZN', direction: 'Long', entry: 198.20, exit: 204.80, size: 100, pnl: 660, pnlPct: 3.33, rr: 2.6, strategy: 'Momentum', timeframe: '15m', rating: 4, hasScreenshot: true, notes: 'Strong momentum after earnings. Rode the 9 EMA.', lessons: '', status: 'Reviewed', dateISO: '2026-08-20' },
  { id: 'trade-007', date: 'Aug 19', symbol: 'MSFT', direction: 'Long', entry: 420.10, exit: 432.40, size: 60, pnl: 738, pnlPct: 2.93, rr: 3.1, strategy: 'Breakout', timeframe: '1D', rating: 5, hasScreenshot: true, notes: 'ATH breakout. Held overnight. Perfect trade.', lessons: '', status: 'Reviewed', dateISO: '2026-08-19' },
  { id: 'trade-008', date: 'Aug 18', symbol: 'COIN', direction: 'Long', entry: 241.80, exit: 251.60, size: 90, pnl: 882, pnlPct: 4.05, rr: 2.7, strategy: 'Momentum', timeframe: '15m', rating: 4, hasScreenshot: true, notes: 'Crypto sympathy play. COIN followed BTC breakout.', lessons: '', status: 'Pending Review', dateISO: '2026-08-18' },
  { id: 'trade-009', date: 'Aug 15', symbol: 'GOOGL', direction: 'Short', entry: 178.40, exit: 180.20, size: 110, pnl: -198, pnlPct: -1.01, rr: -0.6, strategy: 'Mean Reversion', timeframe: '5m', rating: 2, hasScreenshot: false, notes: 'Faded a move that was still in trend. Bad read.', lessons: 'Never fade a strong trend.', status: 'Flagged', dateISO: '2026-08-15' },
  { id: 'trade-010', date: 'Aug 14', symbol: 'AMD', direction: 'Long', entry: 162.40, exit: 158.80, size: 140, pnl: -504, pnlPct: -2.22, rr: -1.2, strategy: 'Breakout', timeframe: '1H', rating: 1, hasScreenshot: true, notes: 'Bought the breakout but market reversed hard. Stop hit.', lessons: 'Check broader market context before entering.', status: 'Flagged', dateISO: '2026-08-14' },
  { id: 'trade-011', date: 'Aug 13', symbol: 'NFLX', direction: 'Short', entry: 680.20, exit: 668.40, size: 30, pnl: 354, pnlPct: 1.74, rr: 2.2, strategy: 'Gap Fill', timeframe: '30m', rating: 4, hasScreenshot: true, notes: 'Gap from earnings filled perfectly. Textbook setup.', lessons: '', status: 'Reviewed', dateISO: '2026-08-13' },
  { id: 'trade-012', date: 'Aug 12', symbol: 'NVDA', direction: 'Short', entry: 134.80, exit: 138.60, size: 200, pnl: -760, pnlPct: -2.82, rr: -1.4, strategy: 'Mean Reversion', timeframe: '15m', rating: 1, hasScreenshot: true, notes: 'Shorted into strong trend. Lesson: never fade NVDA momentum.', lessons: 'Never short a stock in a strong uptrend.', status: 'Flagged', dateISO: '2026-08-12' },
];

// ─── DB row → StoredTrade ────────────────────────────────────────────────────
function rowToTrade(row: any): StoredTrade {
  return {
    id: row.id,
    date: row.date,
    dateISO: row.date_iso,
    symbol: row.symbol,
    direction: row.direction as 'Long' | 'Short',
    entry: Number(row.entry),
    exit: Number(row.exit),
    size: Number(row.size),
    pnl: Number(row.pnl),
    pnlPct: Number(row.pnl_pct),
    rr: Number(row.rr),
    strategy: row.strategy,
    timeframe: row.timeframe || '',
    stopLoss: row.stop_loss != null ? Number(row.stop_loss) : undefined,
    takeProfit: row.take_profit != null ? Number(row.take_profit) : undefined,
    rating: row.rating,
    hasScreenshot: row.has_screenshot,
    notes: row.notes || '',
    lessons: row.lessons || '',
    status: row.status as StoredTrade['status'],
  };
}

// ─── StoredTrade → DB row ────────────────────────────────────────────────────
function tradeToRow(trade: StoredTrade, userId: string) {
  return {
    id: trade.id,
    user_id: userId,
    date: trade.date,
    date_iso: trade.dateISO,
    symbol: trade.symbol,
    direction: trade.direction,
    entry: trade.entry,
    exit: trade.exit,
    size: trade.size,
    pnl: trade.pnl,
    pnl_pct: trade.pnlPct,
    rr: trade.rr,
    strategy: trade.strategy,
    timeframe: trade.timeframe || '',
    stop_loss: trade.stopLoss ?? null,
    take_profit: trade.takeProfit ?? null,
    rating: trade.rating,
    has_screenshot: trade.hasScreenshot,
    notes: trade.notes || '',
    lessons: trade.lessons || '',
    status: trade.status,
  };
}

// ─── localStorage helpers (fallback when not authenticated) ─────────────────
function lsLoad(): StoredTrade[] {
  if (typeof window === 'undefined') return SEED_TRADES;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(SEED_TRADES));
      return SEED_TRADES;
    }
    return JSON.parse(raw) as StoredTrade[];
  } catch {
    return SEED_TRADES;
  }
}

function lsSave(trades: StoredTrade[]): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(trades));
}

// ─── Supabase helpers ────────────────────────────────────────────────────────
async function getAuthUser() {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  return { supabase, user };
}

// ─── Public API ──────────────────────────────────────────────────────────────

export async function loadTradesAsync(): Promise<StoredTrade[]> {
  try {
    const { supabase, user } = await getAuthUser();
    if (!user) return lsLoad();

    const { data, error } = await supabase
      .from('trades')
      .select('*')
      .eq('user_id', user.id)
      .order('date_iso', { ascending: false });

    if (error) {
      console.log('loadTradesAsync error:', error.message);
      return lsLoad();
    }

    // If user has no trades yet, seed with defaults
    if (!data || data.length === 0) {
      await seedTradesForUser(supabase, user.id);
      return SEED_TRADES;
    }

    return data.map(rowToTrade);
  } catch {
    return lsLoad();
  }
}

async function seedTradesForUser(supabase: any, userId: string) {
  try {
    const rows = SEED_TRADES.map((t) => tradeToRow(t, userId));
    await supabase.from('trades').insert(rows);
  } catch {
    // ignore seed errors
  }
}

export function loadTrades(): StoredTrade[] {
  return lsLoad();
}

export function saveTrades(trades: StoredTrade[]): void {
  lsSave(trades);
}

export async function addTradeAsync(trade: StoredTrade): Promise<StoredTrade[]> {
  try {
    const { supabase, user } = await getAuthUser();
    if (!user) {
      const existing = lsLoad();
      const updated = [trade, ...existing];
      lsSave(updated);
      return updated;
    }

    await supabase.from('trades').insert(tradeToRow(trade, user.id));
    return loadTradesAsync();
  } catch {
    const existing = lsLoad();
    const updated = [trade, ...existing];
    lsSave(updated);
    return updated;
  }
}

export function addTrade(trade: StoredTrade): StoredTrade[] {
  const existing = lsLoad();
  const updated = [trade, ...existing];
  lsSave(updated);
  return updated;
}

export async function updateTradeAsync(updated: StoredTrade): Promise<StoredTrade[]> {
  try {
    const { supabase, user } = await getAuthUser();
    if (!user) {
      const existing = lsLoad();
      const trades = existing.map((t) => (t.id === updated.id ? updated : t));
      lsSave(trades);
      return trades;
    }

    await supabase
      .from('trades')
      .update(tradeToRow(updated, user.id))
      .eq('id', updated.id)
      .eq('user_id', user.id);

    return loadTradesAsync();
  } catch {
    const existing = lsLoad();
    const trades = existing.map((t) => (t.id === updated.id ? updated : t));
    lsSave(trades);
    return trades;
  }
}

export function updateTrade(updated: StoredTrade): StoredTrade[] {
  const existing = lsLoad();
  const trades = existing.map((t) => (t.id === updated.id ? updated : t));
  lsSave(trades);
  return trades;
}

export async function deleteTradeAsync(id: string): Promise<StoredTrade[]> {
  try {
    const { supabase, user } = await getAuthUser();
    if (!user) {
      const existing = lsLoad();
      const trades = existing.filter((t) => t.id !== id);
      lsSave(trades);
      return trades;
    }

    await supabase.from('trades').delete().eq('id', id).eq('user_id', user.id);
    return loadTradesAsync();
  } catch {
    const existing = lsLoad();
    const trades = existing.filter((t) => t.id !== id);
    lsSave(trades);
    return trades;
  }
}

export function deleteTrade(id: string): StoredTrade[] {
  const existing = lsLoad();
  const trades = existing.filter((t) => t.id !== id);
  lsSave(trades);
  return trades;
}

export async function deleteTradesBulkAsync(ids: string[]): Promise<StoredTrade[]> {
  try {
    const { supabase, user } = await getAuthUser();
    if (!user) {
      const existing = lsLoad();
      const trades = existing.filter((t) => !ids.includes(t.id));
      lsSave(trades);
      return trades;
    }

    await supabase.from('trades').delete().in('id', ids).eq('user_id', user.id);
    return loadTradesAsync();
  } catch {
    const existing = lsLoad();
    const trades = existing.filter((t) => !ids.includes(t.id));
    lsSave(trades);
    return trades;
  }
}

export function deleteTradesBulk(ids: string[]): StoredTrade[] {
  const existing = lsLoad();
  const trades = existing.filter((t) => !ids.includes(t.id));
  lsSave(trades);
  return trades;
}

export function generateTradeId(): string {
  return `trade-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
}

export function exportTradesToCSV(trades: StoredTrade[]): void {
  const headers = ['Date', 'Symbol', 'Direction', 'Entry', 'Exit', 'Size', 'P&L ($)', 'P&L (%)', 'R:R', 'Strategy', 'Timeframe', 'Rating', 'Status', 'Notes'];
  const rows = trades.map((t) => [
    t.dateISO,
    t.symbol,
    t.direction,
    t.entry.toFixed(2),
    t.exit.toFixed(2),
    t.size,
    t.pnl.toFixed(2),
    t.pnlPct.toFixed(2),
    t.rr.toFixed(2),
    t.strategy,
    t.timeframe || '',
    t.rating,
    t.status,
    `"${(t.notes || '').replace(/"/g, '""')}"`,
  ]);
  const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `trade-journal-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
